import ApiModel from "../../../models/ApiModel";
import StorageLocal from "../../../../inc/StorageLocal";
import { store } from "../../../reducers";
import { setAuthData, clearAuthData } from "../../../reducers/app/actions";

// ЕДИНСТВЕННАЯ точка логина/логаута/refresh. Компоненты токены напрямую
// не читают и не пишут — только через ApiAuth (из useAuth) и state.app.
//
// Токены персистятся в localStorage (ключ StorageKey) — осознанный компромисс:
// при XSS токены доступны злоумышленнику; принято, задокументировано в ТЗ.

// Единый in-flight Promise (НЕ булев флаг): параллельные 401 ждут ОДИН запрос
// refresh — иначе гонка сжигает ротированный refresh-токен.
let refreshTokenPromise = null;

export default class ApiAuth extends ApiModel {
    static StorageKey = 'CLOUD_PHONE_AUTH';

    pathDef = 'public/auth';
    __showErrors = false; // страница логина показывает ошибки инлайн

    // POST public/auth/login {username, password} →
    // {access_token, refresh_token, permissions, roles, user}.
    // Возвращает {result, code, errors, message} для формы логина.
    static async Login({ username, password }) {
        const api = new ApiAuth();
        const answer = await api._query('public/auth/login', 'POST', { username, password });
        api.code = answer.code;
        if (answer.result && answer.response && answer.response.access_token) {
            ApiAuth.SetInitData(answer.response);
            return { result: true, code: answer.code, errors: null, message: null };
        }
        let message = (answer.response && (answer.response.message || answer.response.error)) || null;
        if (answer.code === -1) message = 'Отсутствует подключение к серверу';
        if (answer.code === 401 && !message) message = 'Неверный логин или пароль';
        if (answer.code === 429 && !message) message = 'Слишком много попыток входа, подождите минуту';
        return {
            result: false,
            code: answer.code,
            errors: (answer.response && answer.response.errors) ? answer.response.errors : null,
            message,
        };
    }

    // POST public/auth/refresh-token {access_token, refresh_token} → новая пара (ротация).
    // 401/403/422 от самого refresh — ТЕРМИНАЛЬНЫ: сразу Logout, без рекурсии.
    static async RefreshToken() {
        if (refreshTokenPromise) return refreshTokenPromise;
        refreshTokenPromise = (async () => {
            try {
                const api = new ApiAuth();
                const accessToken = api.getAccessToken();
                const refreshToken = api.getRefreshToken();
                if (!refreshToken) {
                    await ApiAuth.Logout();
                    return false;
                }
                const answer = await api._query('public/auth/refresh-token', 'POST', {
                    access_token: accessToken,
                    refresh_token: refreshToken,
                });
                if (answer.result && answer.response && answer.response.access_token) {
                    ApiAuth.SetInitData(answer.response);
                    return true;
                }
                if (answer.code === 401 || answer.code === 403 || answer.code === 422) {
                    await ApiAuth.Logout();
                }
                return false;
            } finally {
                refreshTokenPromise = null;
            }
        })();
        return refreshTokenPromise;
    }

    // POST public/auth/logout {access_token}; локальный сброс — в finally,
    // даже если сеть упала. После — редирект на /login.
    static async Logout() {
        try {
            const api = new ApiAuth();
            const accessToken = api.getAccessToken();
            if (accessToken) {
                await api._query('public/auth/logout', 'POST', { access_token: accessToken });
            }
        } finally {
            StorageLocal.Remove(ApiAuth.StorageKey);
            store.dispatch(clearAuthData());
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
    }

    // Сохранение пары токенов + прав: redux + StorageLocal.
    // Ответ refresh содержит только пару — permissions/roles/user берём из state.
    static SetInitData(data) {
        const stateApp = store.getState().app;
        const authData = {
            isAuth: true,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            expAccessToken: ApiAuth.ParseTokenExpiration(data.access_token),
            expRefreshToken: ApiAuth.ParseTokenExpiration(data.refresh_token),
            userPermissions: data.permissions ?? stateApp.userPermissions,
            userRoles: data.roles ?? stateApp.userRoles,
            user: data.user ?? stateApp.user,
        };
        StorageLocal.Set(ApiAuth.StorageKey, authData);
        store.dispatch(setAuthData(authData));
    }

    // exp (unix-время) из payload JWT; не JWT / нет exp → null.
    static ParseTokenExpiration(token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            return payload.exp ?? null;
        } catch (error) {
            return null;
        }
    }
}
